import React, { useState, useEffect } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading, Subheading as SubheadingBase } from "../components/misc/Headings";
import { PrimaryButton as PrimaryButtonBase } from "../components/misc/Buttons";
import EmailIllustrationSrc from "../images/email-illustration.svg";

const Container = tw.div`relative`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-20 md:py-24`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 h-80 md:h-auto`;
const TextColumn = styled(Column)((props) => [
  tw`md:w-7/12 mt-16 md:mt-0`,
  props.textOnLeft
    ? tw`md:mr-12 lg:mr-16 md:order-first`
    : tw`md:ml-12 lg:ml-16 md:order-last`,
    props.textOnLeft && tw`ml-4`,
]);

const Image = styled.div((props) => [
  `background-image: url("${props.imageSrc}");`,
  tw`rounded bg-contain bg-no-repeat bg-center h-full`
]);
const TextContent = tw.div`lg:py-8 text-center md:text-left`;
  
  const Subheading = tw(SubheadingBase)`text-center md:text-left`;
  const Heading = tw(SectionHeading)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
  const Description = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;
  
  const Form = tw.form`mt-8 md:mt-10 text-sm flex flex-col lg:flex-row flex-wrap lg:flex-nowrap`;
  const Input = tw.input`mb-4 border-2 px-5 py-3 rounded focus:outline-none font-medium transition duration-300 hocus:border-primary-500 flex-1`;
  const FullWidthInput = tw(Input)`w-full mb-4 lg:mb-0`; // Full width for mobile and OTP inputs
  
  const SubmitButton = tw(PrimaryButtonBase)`inline-block lg:ml-6 mt-6 lg:mt-0 w-full lg:w-auto`;
  const ResendButton = tw(PrimaryButtonBase)`inline-block lg:ml-6 mt-6 lg:mt-0 w-full lg:w-auto`;
  
  const CountdownMessage = styled.span`
    ${tw`text-center text-sm text-gray-500 mt-2 font-bold`}
    margin-top: 1rem; // Add top margin
    margin-left: 0.5rem; // Add left margin
  `;
  
  const GreyButton = styled.button`
    ${tw`bg-gray-500 text-white font-bold py-2 px-4 rounded`}
    ${props => props.disabled ? tw`cursor-not-allowed` : tw`cursor-pointer`}
    transition: background-color 0.3s;
  
    &:hover {
      ${props => props.disabled ? tw`bg-gray-500` : tw`bg-gray-700`}
    }
  `;
  
  export default ({
    subheading = "Forgot Password?",
    heading = (
      <>
        Need to <span tw="text-primary-500">reset your password</span>?
      </>
    ),
    description = "Enter your mobile number below and we'll send you an OTP to reset your password.",
    submitButtonText = "Send OTP",
    otpPlaceholder = "Enter your mobile number",
    resendButtonText = "Resend OTP",
    formAction = "#",
    formMethod = "post",
    textOnLeft = true
  }) => {
    const [timer, setTimer] = useState(30); // Initial timer value in seconds
    const [otpSent, setOtpSent] = useState(false);
    const [showResendButton, setShowResendButton] = useState(false);
    const [showSendButton, setShowSendButton] = useState(true); 
    const [showSubmitButton, setShowSubmitButton] = useState(false);
    const [otpInput, setOtpInput] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
  
    useEffect(() => {
      setShowResendButton(mobileNumber.length === 10 && !otpSent);
    }, [mobileNumber, otpSent]);
  
    useEffect(() => {
      let intervalId;
  
      if (otpSent && timer > 0) {
        intervalId = setInterval(() => {
          setTimer((prevTimer) => prevTimer - 1);
        }, 1000);
      }
  
      if (timer === 0) {
        setShowResendButton(true);
        setShowSubmitButton(true);
      }
  
      return () => {
        clearInterval(intervalId);
      };
    }, [otpSent, timer]);
  
    const handleResendOTP = () => {
      setTimer(30);
      setOtpSent(true);
      setShowResendButton(false);
      setOtpInput('');
    };
  
    const handleSubmitOTP = (e) => {
      e.preventDefault();
      // Add logic to verify OTP or perform necessary actions
    };
    
    return (
      <Container>
        <TwoColumn>
          <ImageColumn>
            <Image imageSrc={EmailIllustrationSrc} />
          </ImageColumn>
          <TextColumn textOnLeft={textOnLeft}>
            <TextContent>
              {subheading && <Subheading>{subheading}</Subheading>}
              <Heading>{heading}</Heading>
              <Description>{description}</Description>
              {otpSent ? (
                <Form onSubmit={handleSubmitOTP}>
                <Input
                  type="text"
                  name="otp"
                  value={otpInput}
                  placeholder={otpPlaceholder}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (/^\d*$/.test(input)) { // Check if input contains only digits
                      setOtpInput(input);
                    }
                  }}
                  pattern="[0-9]*"
                  minLength="10" // Allow only numbers
                  maxLength="10"
                  required
                />
                <SubmitButton type="submit">Submit</SubmitButton>
              </Form>
              ) : (
                <Form action={formAction} method={formMethod}>
                  <FullWidthInput
                    type="text"
                    name="otp"
                    value={otpInput}
                    placeholder={otpPlaceholder}
                    onChange={(e) => {
                      const input = e.target.value;
                      if (/^\d*$/.test(input)) { // Check if input contains only digits
                        setOtpInput(input);
                      }
                    }}
                    pattern="[0-9]*" 
                    minLength="10"
                    maxLength="10"// Allow only numbers
                    required
                  />
                  <SubmitButton
                    type="button"
                    onClick={() => {
                      setOtpSent(true);
                      setShowResendButton(false);
                      setShowSendButton(false);
                      setShowSubmitButton(true);
                    }}
                  >
                    {submitButtonText}
                  </SubmitButton>
                </Form>
              )}
              {otpSent && timer > 0 && (
                <CountdownMessage>Resend OTP in {Math.floor(timer / 60)}:{timer % 60 < 10 ? `0${timer % 60}` : timer % 60} minutes</CountdownMessage>
              )}
              {showResendButton && (
                <GreyButton type="button" onClick={handleResendOTP} disabled={timer > 0}>
                  Resend OTP
                </GreyButton>
              )}
            </TextContent>
          </TextColumn>
        </TwoColumn>
      </Container>
    );
  };
    
